import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appVersion, setAppVersion] = useState('loading...');

  // Fetch version from server
  useEffect(() => {
    fetch('/api/version')
      .then(res => res.json())
      .then(data => setAppVersion(data.version))
      .catch(() => setAppVersion('unknown'));
  }, []);

  // Resolve legacy plan names to display names
  const getPlanDisplayName = (plan) => {
    const planMap = {
      'pro': 'Starter',
      'gold': 'Professional',
      'platinum': 'Enterprise',
      'starter': 'Starter',
      'growth': 'Professional',
      'enterprise': 'Enterprise',
      'free': 'Free'
    };
    return planMap[plan?.toLowerCase()] || plan || 'Free';
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="main-layout">
      {/* Hamburger Button */}
      <button
        className="hamburger-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Martech Monitoring</h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
            {getPlanDisplayName(user?.plan)} Plan
          </p>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/dashboard"
            className={isActive('/dashboard') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>📊</span> Dashboard
          </Link>

          <Link
            to="/sfmc-accounts"
            className={isActive('/sfmc-accounts') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>🔗</span> SFMC Accounts
          </Link>

          <Link
            to="/monitoring"
            className={isActive('/monitoring') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>🔍</span> Monitoring
          </Link>

          <Link
            to="/explorer"
            className={isActive('/explorer') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>🗂️</span> SFMC Explorer
          </Link>

          <Link
            to="/trends"
            className={isActive('/trends') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>📈</span> Trends & History
          </Link>

          <Link
            to="/alerts"
            className={isActive('/alerts') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>📧</span> Alert History
          </Link>

          <Link
            to="/audit"
            className={isActive("/audit") ? "active" : ""}
            onClick={closeSidebar}
          >
            <span>🔍</span> Operations Audit
          </Link>

          <Link
            to="/reports"
            className={isActive('/reports') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>📊</span> Reports
          </Link>

          <Link
            to="/profile"
            className={isActive('/profile') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>👤</span> Profile
          </Link>

          <Link
            to="/settings"
            className={isActive('/settings') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>⚙️</span> Settings
          </Link>

          <Link
            to="/billing"
            className={isActive('/billing') ? 'active' : ''}
            onClick={closeSidebar}
          >
            <span>💳</span> Billing
          </Link>

          {(user?.role === 'admin' || user?.role === 'owner') && (
            <Link
              to="/admin"
              className={location.pathname.startsWith('/admin') ? 'active' : ''}
              onClick={closeSidebar}
              style={{
                marginTop: '20px',
                borderTop: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
                paddingTop: '20px'
              }}
            >
              <span>⚙️</span> Admin Panel
            </Link>
          )}
        </nav>

        <div className="sidebar-footer">
          <div style={{ marginBottom: '15px', fontSize: '14px' }}>
            <div style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>Logged in as</div>
            <div style={{ color: darkMode ? 'white' : '#1f2937', fontWeight: '500' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '12px' }}>{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{ width: '100%' }}
          >
            Sign Out
          </button>
          <div style={{ marginTop: '15px', textAlign: 'center', fontSize: '11px', color: darkMode ? '#6b7280' : '#9ca3af' }}>
            v{appVersion}
          </div>
        </div>
      </div>

      <div className="main-content">
        {children}
      </div>
    </div>
  );
}

export default Layout;
